import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { Message, MessagePage } from "@/api/types";

const PAGE = 50;
const GAP_MAX = 100; // 서버 limit 상한. 더 벌어지면 불러온 이전 메시지를 버리고 다시 받는다
const RETRY_AFTER_MS = 3000; // 실패 뒤 스크롤마다 재요청하지 않게
const EMPTY: Message[] = [];

/**
 * 채팅방 이전 메시지(대화 목록 설계 D6). GET /conversations 의 최근 창(recent) 앞에 GET /conversations/:id/messages?before= 로 받은 것을 붙인다.
 * 창이 다시 받아져 앞으로 밀리면(새 턴·재조회) 이미 받은 이전 메시지와 창 사이 빈 구간을 메운다.
 */
export function useOlderMessages(conversationId: string | undefined, recent: readonly Message[]) {
  // 받은 이전 메시지는 어느 대화의 것인지와 함께 둔다. 대화가 바뀌거나 창과 너무 벌어지면 렌더 중에 비운다(effect 안 동기 setState 대신)
  const [store, setStore] = useState<{ conv: string | undefined; list: Message[] }>({ conv: conversationId, list: [] });
  const [loading, setLoading] = useState(false);
  const failedAt = useRef(0);

  const windowFirst = recent[0]?.seq ?? 1;
  const storedLast = store.list.at(-1)?.seq;
  const stale = store.conv !== conversationId || (storedLast !== undefined && windowFirst - 1 - storedLast > GAP_MAX);
  if (stale) setStore({ conv: conversationId, list: [] });
  const older = stale ? EMPTY : store.list;
  const setOlder = useCallback(
    (update: (prev: Message[]) => Message[]) => setStore((s) => (s.conv === conversationId ? { conv: s.conv, list: update(s.list) } : s)),
    [conversationId],
  );

  const lastOlder = older.at(-1)?.seq;
  const oldest = older[0]?.seq ?? windowFirst;
  const hasMore = Boolean(conversationId) && oldest > 1;

  const fetchPage = useCallback(
    (before: number, limit: number) => apiFetch<MessagePage>(`${ep.messages(conversationId!)}?before=${before}&limit=${limit}`),
    [conversationId],
  );

  useEffect(() => {
    if (!conversationId || lastOlder === undefined) return;
    const gap = windowFirst - 1 - lastOlder;
    if (gap <= 0 || gap > GAP_MAX) return; // GAP_MAX 초과는 위 렌더 단계에서 이미 비웠다
    let live = true;
    fetchPage(windowFirst, gap)
      .then((page) => live && setOlder((prev) => [...prev, ...page.messages.filter((m) => m.seq > (prev.at(-1)?.seq ?? 0))]))
      .catch(() => live && setOlder(() => []));
    return () => {
      live = false;
    };
  }, [conversationId, windowFirst, lastOlder, fetchPage, setOlder]);

  const loadOlder = useCallback(async () => {
    if (!hasMore || loading || Date.now() - failedAt.current < RETRY_AFTER_MS) return;
    setLoading(true);
    try {
      const page = await fetchPage(oldest, PAGE);
      setOlder((prev) => [...page.messages.filter((m) => m.seq < (prev[0]?.seq ?? windowFirst)), ...prev]);
    } catch {
      failedAt.current = Date.now();
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, fetchPage, oldest, windowFirst, setOlder]);

  const messages = useMemo(() => (older.length ? [...older.filter((m) => m.seq < windowFirst), ...recent] : recent), [older, recent, windowFirst]);
  return { messages, hasMore, loading, loadOlder };
}
